'use strict';

import {Helper} from '../helpers/Helper.js';

export class DonationOrderMetabox {
	#productsSelect;
	#productsOption;
	#productsName;
	#productTypeSelect;
	#productsByType = {};
	#storefrontSelect;
	#storefrontChoices;
	#selectedStorefront = 0;

	/**
	 * @type {Choices}
	 */
	#productsChoices;

	constructor() {
		this.#productsName = document.getElementById('levcharity_order_product_name');
		this.#productTypeSelect = document.getElementById('levcharity_offline_product_type');
		this.#selectProductTypes();
		this.#watchProductType();
		this.#initStorefrontSelect();
	};

	debounce(func, delay = 500) {
        let timeoutId;
        return function (...args) {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                func.apply(this, args);
            }, delay);
        };
    }

	async #watchProductType() {
		if(!this.#productTypeSelect) {
			return;
		}

		const helper = new Helper();
		const initialProductType = this.#productTypeSelect.value;

		// Only load products initially if not e-com, or if e-com but no storefronts exist
		if (initialProductType && initialProductType !== 'e-com') {
			await helper.getProductByType(initialProductType, true, false, "", this.#selectedStorefront).then(response => {
				this.#productsByType[initialProductType] = {
					timestamp: Helper.strtotime("now"),
					products: response,
				};
				this.#updateProducts(this.#productsByType[initialProductType]?.products || []);
			});
		} else if (initialProductType === 'e-com') {
			// For e-com, check if storefronts exist first
			this.#showStorefrontSelect();
			await this.#checkAndLoadStorefronts();
		}

		this.#productTypeSelect.addEventListener("change", async (event) => {
			const productType = event.target.value;
			const helper = new Helper();

			if (productType === 'e-com') {
				this.#showStorefrontSelect();
				await this.#checkAndLoadStorefronts();
				this.#updateProducts([]);
			} else {
				this.#hideStorefrontSelect();
				this.#selectedStorefront = 0;

				if(productType) {
					if(
						typeof this.#productsByType[productType] === "undefined" ||
						Helper.strtotime('now') - this.#productsByType[productType].timestamp > 120
					) {
						await helper.getProductByType(productType, true, false, "", this.#selectedStorefront).then(response => {
							this.#productsByType[productType] = {
								timestamp: Helper.strtotime('now'),
								products: response,
							};
						});
					}
					this.#updateProducts(this.#productsByType[productType]?.products || []);
				} else {
					this.#productsByType = [];
					this.#updateProducts([]);
				}
			}
		});
	}

	#updateProducts(options) {
		this.#productsChoices?.destroy();

		const allOptions = [...this.#productsName.options].map(option => {
			return {
				value: option.value,
				label: option.label
			};
		});

		const placeholder = allOptions.find(o => o.value === '');

		if (placeholder) {
			this.#productsName.innerHTML = `<option value="">${placeholder.label}</option>`;
		}

		options.forEach(option => {
			this.#productsName.innerHTML += `<option value="${option.value}"${this.#productsName.dataset.selected == option.value ? ' selected' : ''}>${option.label}</option>`;
		});

		this.#productsName.dataset.selected = '';

		this.#selectProductNames();
	}

	async #selectProductTypes() {
		if (!this.#productTypeSelect) {
			return;
		}

		const { default: Choices } = await import(/* webpackChunkName: "choices" */ 'choices.js')
		this.#productTypeSelect.choicesInstance = new Choices(this.#productTypeSelect, {
			shouldSortItems: true
		});
	}


	async #selectProductNames() {
		if (!this.#productsName) {
			return;
		}

		const productOptions = {
			shouldSortItems: true
		};

		const { default: Choices } = await import(/* webpackChunkName: "choices" */ 'choices.js')
		this.#productsChoices = new Choices(
			this.#productsName,
			productOptions
		);
		this.#productsName.choicesInstance = this.#productsChoices;

        const search = async(query) => {
            const helper = new Helper();
			await helper.getProductByType(this.#productTypeSelect.value, true, false, query, this.#selectedStorefront).then(response => {
                if(response) {
					this.#productsName.choicesInstance.setChoices([], "value", "label", true);
					this.#productsName.choicesInstance.setChoices(response, "value", "label", true);
                }
            });
        }, debouncedSearch = this.debounce(search, 300);

        this.#productsName.addEventListener("search", async(e) => {
            e.preventDefault();


			let query = (e.detail && e.detail.value !== undefined) ? e.detail.value : null;
			if(query === null || query === "") {
				const innerInput = this.#productsName.querySelector('.choices__input') || null;
				if(innerInput)
					query = innerInput.value || "";
			}

			debouncedSearch(query);
        });
	}

	#initStorefrontSelect() {
		let storefrontWrapper = document.querySelector('.levcharity_storefront_wrapper');

		if (!storefrontWrapper) {
			const productTypeWrapper = document.querySelector('.levcharity_offline_product_type')?.closest('.form-group');
			if (productTypeWrapper && productTypeWrapper.parentNode) {
				storefrontWrapper = document.createElement('div');
				storefrontWrapper.className = 'form-group levcharity_select_wrapper levcharity_storefront_wrapper';
				storefrontWrapper.style.display = 'none';

				const formField = document.createElement('div');
				formField.className = 'form-field heading_only';

				const select = document.createElement('select');
				select.className = 'levcharity_select';
				select.id = 'levcharity_storefront';
				select.name = 'storefront';

				const placeholder = document.createElement('option');
				placeholder.value = '';
				placeholder.textContent = 'Storefront';
				placeholder.selected = true;
				select.appendChild(placeholder);

				const errorMessage = document.createElement('p');
				errorMessage.className = 'levcharity_error_message';

				formField.appendChild(select);
				formField.appendChild(errorMessage);
				storefrontWrapper.appendChild(formField);

				if (productTypeWrapper.nextSibling) {
					productTypeWrapper.parentNode.insertBefore(storefrontWrapper, productTypeWrapper.nextSibling);
				} else {
					productTypeWrapper.parentNode.appendChild(storefrontWrapper);
				}
			}
		}

		this.#storefrontSelect = document.getElementById('levcharity_storefront');
	}

	#showStorefrontSelect() {
		const wrapper = document.querySelector('.levcharity_storefront_wrapper');
		if (wrapper) {
			wrapper.style.display = '';
		}
	}

	#hideStorefrontSelect() {
		const wrapper = document.querySelector('.levcharity_storefront_wrapper');
		if (wrapper) {
			wrapper.style.display = 'none';
		}
	}

	async #checkAndLoadStorefronts() {
		if (!this.#storefrontSelect) {
			return;
		}

		if (this.#storefrontSelect.dataset.loaded === 'true') {
			return;
		}

		try {
			const response = await fetch('/wp-json/levcharity/ecommerce/v2/storefronts/options');
			const data = await response.json();

			if (data && data.options && Array.isArray(data.options) && data.options.length > 0) {
				await this.#loadStorefronts(data.options);
			} else {
				const helper = new Helper();
				const productType = this.#productTypeSelect.value;
				await helper.getProductByType(productType, true, false, "", 0).then(response => {
					this.#productsByType[productType] = {
						timestamp: Helper.strtotime('now'),
						products: response,
					};
					this.#updateProducts(this.#productsByType[productType]?.products || []);
				});
			}
		} catch (error) {
			console.error('Error loading storefronts:', error);
		}
	}

	async #loadStorefronts(options) {
		if (!this.#storefrontSelect) {
			return;
		}

		if (this.#storefrontChoices) {
			this.#storefrontChoices.destroy();
			this.#storefrontChoices = null;
		}

		this.#storefrontSelect.innerHTML = '<option value="">Storefront</option>';

		options.forEach(option => {
			const opt = document.createElement('option');
			opt.value = option.value;
			opt.textContent = option.label;
			this.#storefrontSelect.appendChild(opt);
		});

		const { default: Choices } = await import(/* webpackChunkName: "choices" */ 'choices.js');
		this.#storefrontChoices = new Choices(this.#storefrontSelect, {
			shouldSortItems: true
		});

		this.#storefrontSelect.dataset.loaded = 'true';

		this.#storefrontSelect.addEventListener('change', async (event) => {
			this.#selectedStorefront = parseInt(event.target.value) || 0;
			if (this.#selectedStorefront > 0) {
				await this.#refreshProducts();
			} else {
				this.#updateProducts([]);
			}
		});
	}

	async #refreshProducts() {
		if (!this.#productTypeSelect || !this.#productTypeSelect.value) {
			return;
		}

		const helper = new Helper();
		const productType = this.#productTypeSelect.value;

		await helper.getProductByType(productType, true, false, "", this.#selectedStorefront).then(response => {
			this.#productsByType[productType] = {
				timestamp: Helper.strtotime('now'),
				products: response,
			};
			this.#updateProducts(this.#productsByType[productType]?.products || []);
		});
	}
}
